import { useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  type BulkClaimResult,
  type BulkClaimRowResult,
  type ClaimCsvRow,
  adminClaim,
  enrolAccount,
} from '~/services/LuxLoyalty';

const BATCH_SIZE = 5;

interface SubmitClaimsParams {
  campaignId: string;
  rows: Array<ClaimCsvRow>;
  enrolMember?: boolean;
}

interface UseSubmitClaimsOptions {
  onSuccess?: (data: BulkClaimResult) => void;
  onError?: (error: Error) => void;
}

async function claimRow(campaignId: string, row: ClaimCsvRow): Promise<BulkClaimRowResult> {
  try {
    const response = await adminClaim(campaignId, {
      customerId: row.customerId,
      ...(row.code ? { code: row.code } : {}),
    });
    return {
      customerId: row.customerId,
      success: true,
      claimRecordId: response.result.claimRecordId,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { customerId: row.customerId, success: false, error: message };
  }
}

async function enrolBatch(rows: Array<ClaimCsvRow>): Promise<void> {
  await Promise.all(
    rows.map(async (row) => {
      try {
        await enrolAccount(row.customerId);
      } catch {
        // Ignore enrolment errors — customer may already be enrolled
      }
    }),
  );
}

export function useSubmitClaims(options: UseSubmitClaimsOptions = {}) {
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const processedRef = useRef(0);

  const mutation = useMutation({
    mutationFn: async ({ campaignId, rows, enrolMember }: SubmitClaimsParams): Promise<BulkClaimResult> => {
      const results: Array<BulkClaimRowResult> = [];
      processedRef.current = 0;
      setProgress({ processed: 0, total: rows.length });

      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);

        if (enrolMember) {
          await enrolBatch(batch);
        }

        const batchResults = await Promise.all(batch.map((row) => claimRow(campaignId, row)));
        results.push(...batchResults);
        processedRef.current += batch.length;
        setProgress({ processed: processedRef.current, total: rows.length });
      }

      const successCount = results.filter((r) => r.success).length;
      const failedCount = results.filter((r) => !r.success).length;

      return {
        totalProcessed: results.length,
        successCount,
        failedCount,
        results,
      };
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });

  return { ...mutation, progress };
}
